import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Library,
  Search,
  X,
  Check,
  Sparkles,
  Copy,
  Plus,
  Trash2,
  BookmarkCheck,
  Info,
  Loader2,
  Database,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  CURATED_SMS_PRESETS,
  SMS_PRESET_CATEGORIES,
} from '../../constants/smsPresetsLibrary';
import playerProfileService from '../../apis/playerProfileService';

export default function SmsPresetsLibraryModal({
  isOpen,
  onClose,
  onSelectPreset,
  onOpenOptimizer = null,
  currentDraftMessage = '',
}) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  // Database-backed presets list
  const [presetsList, setPresetsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDbSynced, setIsDbSynced] = useState(false);

  // Custom Saved Presets Form
  const [isSavingCustom, setIsSavingCustom] = useState(false);
  const [newPresetTitle, setNewPresetTitle] = useState('');
  const [newPresetCategory, setNewPresetCategory] = useState('custom');
  const [newPresetBody, setNewPresetBody] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Fetch presets from database
  const loadPresets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await playerProfileService.getSmsPresets();
      const items = Array.isArray(res?.data)
        ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data : null);

      if (items && items.length > 0) {
        setPresetsList(items);
        setIsDbSynced(true);
      } else {
        // Fallback to initial curated presets if empty or offline
        setPresetsList(CURATED_SMS_PRESETS);
        setIsDbSynced(false);
      }
    } catch (e) {
      console.warn('[SmsPresets] Falling back to bundled presets:', e);
      setPresetsList(CURATED_SMS_PRESETS);
      setIsDbSynced(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadPresets();
    }
  }, [isOpen, loadPresets]);

  // Combined & formatted list for display
  const allPresets = useMemo(() => {
    return presetsList.map((p) => {
      let tagsArr = [];
      if (Array.isArray(p.tags)) {
        tagsArr = p.tags;
      } else if (typeof p.tags === 'string' && p.tags) {
        tagsArr = p.tags.split(',').map((t) => t.trim());
      }
      return {
        ...p,
        tags: tagsArr,
        isCustom: !p.is_system,
      };
    });
  }, [presetsList]);

  // Filtered presets
  const filteredPresets = useMemo(() => {
    let list = allPresets;

    if (activeCategory !== 'all') {
      if (activeCategory === 'custom') {
        list = list.filter((p) => p.isCustom);
      } else {
        list = list.filter((p) => p.category === activeCategory);
      }
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter(
        (p) =>
          p.title?.toLowerCase().includes(q) ||
          p.body?.toLowerCase().includes(q) ||
          (p.tags && p.tags.some((t) => t.toLowerCase().includes(q)))
      );
    }

    return list;
  }, [allPresets, activeCategory, searchTerm]);

  // Save new custom preset to MySQL database
  const handleSaveCustomPreset = async () => {
    if (!newPresetTitle.trim()) {
      toast.error('Please enter a title for your preset');
      return;
    }
    const bodyToSave = newPresetBody.trim() || currentDraftMessage.trim();
    if (!bodyToSave) {
      toast.error('Please enter the preset message text');
      return;
    }

    setSavingPreset(true);
    try {
      const res = await playerProfileService.createSmsPreset({
        title: newPresetTitle.trim(),
        body: bodyToSave,
        category: newPresetCategory || 'custom',
        tags: 'custom,saved',
      });

      const savedPreset = res?.data?.data !== undefined ? res.data.data : res?.data;

      if (res?.success && savedPreset && (savedPreset.id || savedPreset.title)) {
        toast.success(`Preset "${savedPreset.title || newPresetTitle.trim()}" saved to database!`);
        setPresetsList((prev) => [savedPreset, ...prev.filter((p) => p.id !== savedPreset.id)]);
        setIsSavingCustom(false);
        setNewPresetTitle('');
        setNewPresetBody('');
        setActiveCategory('custom');
      } else {
        toast.error(res?.error || res?.message || 'Failed to save preset to database');
      }
    } catch (err) {
      toast.error(err.message || 'Error saving preset');
    } finally {
      setSavingPreset(false);
    }
  };

  // Delete custom preset from MySQL database
  const handleDeleteCustomPreset = async (preset, e) => {
    e.stopPropagation();
    if (!preset?.id) return;

    setDeletingId(preset.id);
    try {
      const res = await playerProfileService.deleteSmsPreset(preset.id);
      if (res?.success) {
        setPresetsList((prev) => prev.filter((p) => p.id !== preset.id));
        toast.success(`Deleted "${preset.title}" from database`);
      } else {
        toast.error(res?.error || 'Failed to delete preset');
      }
    } catch (err) {
      toast.error(err.message || 'Error deleting preset');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyPreset = (preset, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(preset.body);
    setCopiedId(preset.id);
    toast.success('Preset copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUsePreset = (preset) => {
    onSelectPreset(preset.body);
    toast.success(`Applied "${preset.title}" to message!`);
    onClose();
  };

  const handleOptimizePreset = (preset, e) => {
    e.stopPropagation();
    if (onOpenOptimizer) {
      onSelectPreset(preset.body);
      onClose();
      onOpenOptimizer(preset.body);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[80] p-3 sm:p-4 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-800 animate-in zoom-in-95 duration-150">

        {/* MODAL HEADER: Black background, yellow accent, white text */}
        <div className="px-5 sm:px-6 py-4 bg-black text-white flex items-center justify-between border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-400 shadow-inner">
              <Library size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white tracking-wide">
                  Message Presets Library
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/40">
                  {allPresets.length} Templates
                </span>
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-950/60 text-emerald-400 border border-emerald-500/40">
                  <Database size={10} />
                  <span>{isDbSynced ? 'Synced' : 'Loaded'}</span>
                </span>
              </div>
              <p className="text-xs text-gray-300 font-normal">
                Curated & custom SMS message templates stored in Elite Poker Lounge database
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={loadPresets}
              disabled={loading}
              className="text-gray-400 hover:text-amber-400 transition-colors p-1.5 rounded-lg hover:bg-white/10 disabled:opacity-50"
              title="Refresh presets from database"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin text-amber-400' : ''} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-amber-400 transition-colors p-1.5 rounded-lg hover:bg-white/10"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* CONTROLS BAR: SEARCH & SAVE ACTION */}
        <div className="p-3 sm:p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">

          {/* SEARCH INPUT */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search presets by keyword (bounty, cash, classic, food)..."
              className="w-full pl-9 pr-8 py-2 text-xs bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 shadow-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* SAVE CURRENT DRAFT BUTTON */}
          <button
            type="button"
            onClick={() => {
              const nextState = !isSavingCustom;
              setIsSavingCustom(nextState);
              if (nextState && !newPresetBody) {
                setNewPresetBody(currentDraftMessage || '');
              }
            }}
            className="px-3.5 py-2 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 font-semibold rounded-xl text-xs flex items-center justify-center gap-1.5 transition-colors shadow-sm whitespace-nowrap shrink-0 cursor-pointer"
          >
            <Plus size={14} className="text-amber-600 font-bold" />
            <span>Create Custom Preset</span>
          </button>
        </div>

        {/* INLINE CUSTOM PRESET CREATOR FORM */}
        {isSavingCustom && (
          <div className="p-4 bg-amber-50/80 border-b border-amber-200 space-y-3 animate-in fade-in duration-150">
            <div className="flex items-center justify-between">
              <span className="font-bold text-xs text-amber-950 flex items-center gap-1.5">
                <BookmarkCheck size={14} className="text-amber-600" />
                <span>Create New Preset (Saved to MySQL Database)</span>
              </span>
              <button
                type="button"
                onClick={() => setIsSavingCustom(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="sm:col-span-2">
                  <input
                    type="text"
                    value={newPresetTitle}
                    onChange={(e) => setNewPresetTitle(e.target.value)}
                    placeholder="Preset Title (e.g. Friday $5k GTD Special)..."
                    className="w-full px-3 py-2 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-sm"
                  />
                </div>
                <div className="sm:col-span-1">
                  <select
                    value={newPresetCategory}
                    onChange={(e) => setNewPresetCategory(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-sm"
                  >
                    <option value="custom">💾 Custom Saved</option>
                    <option value="tournament">🏆 Tournaments</option>
                    <option value="cash_game">♠️ Cash Games</option>
                    <option value="short">⚡ 160-Char Short</option>
                    <option value="hospitality">🍸 Food & Drinks</option>
                    <option value="notice">📢 Club Notices</option>
                  </select>
                </div>
              </div>

              <div>
                <textarea
                  value={newPresetBody}
                  onChange={(e) => setNewPresetBody(e.target.value)}
                  placeholder="Type or paste the complete SMS preset message here..."
                  rows={3}
                  className="w-full p-2.5 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 font-sans leading-relaxed resize-none shadow-sm"
                />
                <div className="flex items-center justify-between mt-1 text-[11px] text-gray-600">
                  <span className="font-mono">
                    {(newPresetBody || '').length} chars • {((newPresetBody || '').length <= 160) ? '1 segment ⚡' : `${Math.ceil((newPresetBody || '').length / 153)} segments`}
                  </span>
                  {currentDraftMessage && (
                    <button
                      type="button"
                      onClick={() => setNewPresetBody(currentDraftMessage)}
                      className="text-amber-800 hover:text-black font-semibold underline underline-offset-2"
                    >
                      Paste Current Draft Message
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setIsSavingCustom(false)}
                className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCustomPreset}
                disabled={savingPreset}
                className="px-4 py-1.5 text-xs font-bold bg-amber-400 hover:bg-amber-500 text-black rounded-lg shadow-sm flex items-center gap-1.5 disabled:opacity-50"
              >
                {savingPreset ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    <span>Saving to DB...</span>
                  </>
                ) : (
                  <span>Save to Database</span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* CATEGORY TABS BAR */}
        <div className="px-4 py-2.5 bg-gray-100/90 border-b border-gray-200 flex flex-wrap items-center gap-1.5 text-xs">
          {SMS_PRESET_CATEGORIES.map((cat) => {
            const count =
              cat.id === 'all'
                ? allPresets.length
                : cat.id === 'custom'
                  ? allPresets.filter((p) => p.isCustom).length
                  : allPresets.filter((p) => p.category === cat.id).length;

            const isActive = activeCategory === cat.id;

            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 whitespace-nowrap transition-all cursor-pointer ${isActive
                    ? 'bg-black text-amber-300 shadow-sm'
                    : 'bg-white hover:bg-gray-200 text-gray-700 border border-gray-200'
                  }`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${isActive ? 'bg-amber-400/20 text-amber-300' : 'bg-gray-100 text-gray-600'
                    }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* PRESET CARDS GRID */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 bg-gray-50/50">
          {loading ? (
            <div className="py-16 text-center text-xs text-gray-500 space-y-2">
              <Loader2 size={28} className="mx-auto text-amber-500 animate-spin" />
              <p className="font-semibold text-gray-700">Loading presets from database...</p>
            </div>
          ) : filteredPresets.length === 0 ? (
            <div className="py-12 text-center text-xs text-gray-500 space-y-2">
              <Library size={32} className="mx-auto text-gray-300" />
              <p className="font-semibold text-gray-700">No presets found matching your filter</p>
              <p className="text-gray-400">Try searching for other terms or choose "All Presets"</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {filteredPresets.map((preset) => {
                const charLen = preset.body?.length || 0;
                const isSingleSegment = charLen <= 160;

                return (
                  <div
                    key={preset.id}
                    className="bg-white rounded-xl border border-gray-200/90 hover:border-amber-400/80 hover:shadow-md transition-all flex flex-col justify-between overflow-hidden group"
                  >
                    {/* Card Header */}
                    <div className="p-3.5 pb-2.5 border-b border-gray-100 flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="font-bold text-gray-900 text-xs tracking-tight">
                            {preset.title}
                          </h4>
                          {preset.isCustom && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                              Custom DB
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${isSingleSegment
                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                              }`}
                          >
                            {charLen} chars • {isSingleSegment ? '1 segment ⚡' : `${Math.ceil(charLen / 153)} segments`}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => handleCopyPreset(preset, e)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                          title="Copy message text"
                        >
                          {copiedId === preset.id ? (
                            <Check size={13} className="text-emerald-600" />
                          ) : (
                            <Copy size={13} />
                          )}
                        </button>
                        {preset.isCustom && (
                          <button
                            type="button"
                            onClick={(e) => handleDeleteCustomPreset(preset, e)}
                            disabled={deletingId === preset.id}
                            className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-50"
                            title="Delete custom preset from database"
                          >
                            {deletingId === preset.id ? (
                              <Loader2 size={13} className="animate-spin text-rose-500" />
                            ) : (
                              <Trash2 size={13} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Card Body (Message Preview) */}
                    <div className="p-3.5 text-[11px] text-gray-700 font-sans whitespace-pre-wrap leading-relaxed line-clamp-4 flex-1 bg-gray-50/40">
                      {preset.body}
                    </div>

                    {/* Card Footer Actions */}
                    <div className="px-3.5 py-2.5 bg-white border-t border-gray-100 flex items-center justify-between gap-2">
                      {onOpenOptimizer ? (
                        <button
                          type="button"
                          onClick={(e) => handleOptimizePreset(preset, e)}
                          className="px-2.5 py-1 text-[11px] font-bold text-gray-800 hover:text-black bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1 transition-colors"
                          title="Open directly in Claude AI Optimizer"
                        >
                          <Sparkles size={11} className="text-amber-600" />
                          <span>AI Polish</span>
                        </button>
                      ) : <span />}

                      <button
                        type="button"
                        onClick={() => handleUsePreset(preset)}
                        className="px-3 py-1.5 bg-amber-400 hover:bg-amber-500 text-black font-bold rounded-lg text-xs flex items-center gap-1.5 transition-all shadow-sm"
                      >
                        <Check size={13} className="font-bold" />
                        <span>Use Preset</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* MODAL FOOTER */}
        <div className="px-5 sm:px-6 py-3 bg-gray-100 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1.5 text-[11px]">
            <Info size={13} className="text-gray-400" />
            <span>All presets include required 10DLC opt-out compliance phrasing.</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors shadow-sm"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}
